import React from "react";
import cx from "classnames";

import { trDts } from "@modules/datasheets/utils/i18n";
import { MessageBox } from "@common/components/MessageBox";
import { Flex } from "@common/components/Flex";
import { FormControl } from "@common/components/form-control";
import { getTestAttributes } from "@common/utils/tests";
import { useMessage } from "@common/hooks/message";
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from "@common/components/modal";
import { Button } from "@common/components/Button";
import { HttpErrorDto } from "@common/dto/http";
import { DatasheetTableReportFormat } from "../../configure-table.types";

import "./format-name.styles.scss";

export type DatasheetConfigureTableFormatNamProps = {
  onSubmit: (formatName: string) => Promise<unknown> | unknown;
  onClose: () => void;
  checkFormatNameExists: (formatName: string) => Promise<boolean>;
  formats: Array<DatasheetTableReportFormat>;
  onDeleteFormat?: (formatId: number) => Promise<unknown> | unknown;
};

export const FormatName: React.FC<DatasheetConfigureTableFormatNamProps> = (props) => {
  const { onClose, onSubmit, checkFormatNameExists, formats, onDeleteFormat } = props;

  const { closeMessage, showErrorMessage, showConfirmMessage, showInfoMessage, messageProps } = useMessage();

  const getSuccessMessage = React.useCallback((response: unknown): string | null => {
    if (!response) {
      return null;
    }
    if (typeof response === "string") {
      return response;
    }
    if (typeof response === "object") {
      const payload = response as Record<string, unknown>;
      const message = payload.message ?? payload.msg;
      if (typeof message === "string") {
        return message;
      }
      if (typeof payload.data === "string") {
        return payload.data;
      }
      if (payload.data && typeof payload.data === "object") {
        const data = payload.data as Record<string, unknown>;
        const dataMessage = data.message ?? data.msg;
        if (typeof dataMessage === "string") {
          return dataMessage;
        }
      }
    }
    return null;
  }, []);

  const [formatName, setFormatName] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [selectedFormat, setSelectedFormat] = React.useState<DatasheetTableReportFormat | null>(null);

  const handleSubmit = React.useCallback(async () => {
    setSubmitting(true);
    try {
      const isExists = await checkFormatNameExists(formatName);
      if (isExists) {
        showErrorMessage(trDts("The format name already exists"));
        setSubmitting(false);
        return;
      }

      try {
        const response = await onSubmit(formatName.trim());
        const successMessage = getSuccessMessage(response);
        if (successMessage) {
          showInfoMessage(successMessage);
        }
      } catch (err) {
        console.error(err);
        showErrorMessage((err as HttpErrorDto)?.entity || trDts("Failed to save format"));
      } finally {
        setSubmitting(false);
      }
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto)?.entity || trDts("Failed to check format name exists"));
      setSubmitting(false);
    }
  }, [showErrorMessage, formatName, onSubmit, checkFormatNameExists, getSuccessMessage, showInfoMessage]);

  const submittingDisabled = !formatName.trim().length || submitting;

  const handleDeleteFormat = React.useCallback(() => {
    if (!selectedFormat || !onDeleteFormat) {
      return;
    }
    showConfirmMessage({
      header: trDts("Delete Format"),
      content: `${trDts("Confirm delete format")} "${selectedFormat.reportName}"?`,
      confirmText: trDts("Yes"),
      cancelText: trDts("No"),
      onCustomOk: async () => {
        try {
          const response = await onDeleteFormat(selectedFormat.reportNumber);
          const successMessage = getSuccessMessage(response);
          if (successMessage) {
            showInfoMessage(successMessage);
          }
          setSelectedFormat(null);
          setFormatName("");
        } catch (err) {
          console.error(err);
          showErrorMessage((err as HttpErrorDto)?.entity || trDts("Failed to delete format"));
        }
      },
    });
  }, [onDeleteFormat, selectedFormat, showConfirmMessage, showErrorMessage, getSuccessMessage, showInfoMessage]);

  return (
    <Modal
      show
      backdrop="static"
      bsSize="md"
      bsClass="bootstrap modal"
      className="DatasheetElementSettingsConfigureTableFormatName"
      dialogClassName="DatasheetElementSettingsConfigureTableFormatName__dialog"
      animation={false}
      onEscapeKeyDown={onClose}
      onHide={onClose}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle {...getTestAttributes({ id: "fp-table-format-save-as-wnd-1" })}>
          {trDts("Save Table Format")}
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        <MessageBox hideMessageBox={closeMessage} {...messageProps} />
        <Flex className="DatasheetElementSettingsConfigureTableFormatName__content__format-name" alignItems="center">
          <span className="DatasheetElementSettingsConfigureTableFormatName__content__format-name__label">
            {trDts("Format Name")}
          </span>
          <FormControl
            className="DatasheetElementSettingsConfigureTableFormatName__content__format-name__input"
            {...getTestAttributes({ id: "txt-table-format-save-as-wnd-format-name" })}
            value={formatName}
            onChange={(e) => setFormatName(e.target.value)}
          />
        </Flex>
        <div className="DatasheetElementSettingsConfigureTableFormatName__content__formats">
          {formats.map((format, formatIndex) => (
            <Flex
              key={format.reportNumber}
              alignItems="center"
              {...getTestAttributes({ id: `bsgrid-${formatIndex}-0` })}
              onClick={() => {
                setFormatName(format.reportName);
                setSelectedFormat(format);
              }}
              className={cx("DatasheetElementSettingsConfigureTableFormatName__content__formats__item", {
                "DatasheetElementSettingsConfigureTableFormatName__content__formats__item--selected":
                  selectedFormat?.reportNumber === format.reportNumber,
              })}
            >
              <img src="gif/format.gif" />
              <span>{format.reportName}</span>
            </Flex>
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Flex justify="flex-end">
          <Button
            {...getTestAttributes({ id: "btn-table-format-save-as-wnd-delete" })}
            disabled={!selectedFormat || !onDeleteFormat}
            bsStyle="primary"
            onClick={handleDeleteFormat}
          >
            {trDts("Delete Format")}
          </Button>
          <Button {...getTestAttributes({ id: "btn-table-format-save-as-wnd-cancel" })} onClick={onClose}>
            {trDts("Cancel")}
          </Button>
          <Button
            {...getTestAttributes({ id: "btn-table-format-save-as-wnd-save" })}
            disabled={submittingDisabled}
            bsStyle="primary"
            onClick={handleSubmit}
          >
            {submitting ? trDts("Loading") : trDts("Save")}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};
